import React, { useEffect, useState, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { shallowEqual } from 'react-redux';
import theme from '@styles/theme';
import Button from '@components/commons/button/Button';
import { css } from '@emotion/react';
import PopupContainer from '@components/commons/popup/container/PopupContainer';
import { BadgesAcquired, StoryItem } from '@customTypes/story';
import { TopToast } from '@/customTypes/chatStory';
import { useChatPlayContext } from '@provider/ChatPlayProvider';
import { viewAchievementPopup, viewItemPopup } from '@lib/eventManager';
import { useAppSelector } from '@store/hooks';
import { RootState } from '@store/store';

interface IAchievementPopup {
  badgesAcquired?: BadgesAcquired[];
  storyItems?: StoryItem[];
  onConfirm?: () => void;
}

const AchievementPopup = ({
  badgesAcquired,
  storyItems,
  onConfirm,
}: IAchievementPopup) => {
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );
  const { setTopToasts } = useChatPlayContext();
  const { t } = useTranslation();
  const infosRef = useRef<TopToast[]>([]);

  const [info, setInfo] = useState<TopToast>();

  useEffect(() => {
    initInfo();
  }, [badgesAcquired, storyItems]);

  useEffect(() => {
    info?.type === 'achievement'
      ? viewAchievementPopup({
          storyName: playChapterInfo?.playChapter?.story?.name,
          chapterIndex: playChapterInfo?.playChapter?.chapterIndex,
          achievement: info?.name,
        })
      : viewItemPopup({
          storyName: playChapterInfo?.playChapter?.story?.name,
          chapterIndex: playChapterInfo?.playChapter?.chapterIndex,
          item: info?.name,
        });
  }, [info]);

  const initInfo = () => {
    if (badgesAcquired) {
      const badges: TopToast[] = badgesAcquired.map((item) => {
        const badge = item.badge;
        return {
          id: item.badge.aBadgeId,
          type: 'achievement',
          name: badge?.acquired ? badge.display : badge.displayLocked,
          displayName: badge?.acquired ? badge.display : badge.displayLocked,
          description: badge.description,
          imageLink: badge?.acquired
            ? badge?.imageLink
            : badge?.imageLockedLink,
        };
      });

      infosRef.current = badges;
      setInfo(infosRef.current.shift());
    } else if (storyItems) {
      const items: TopToast[] = storyItems.map((item) => {
        return {
          id: item.storyItemId,
          type: 'item',
          name: item.name,
          displayName: item.displayName,
          description: item.desc,
          imageLink: item.imageFile?.link,
          itemCount: item.itemCount,
        };
      });

      infosRef.current = items;
      setInfo(infosRef.current.shift());
    }
  };

  const onShiftItem = (item: TopToast) => {
    setTopToasts((prev) => [...(prev || []), item]);
  };

  const onPressConfirm = () => {
    info && onShiftItem(info);
    if (infosRef.current.length > 0) {
      setInfo(infosRef.current.shift());
    } else {
      onConfirm && onConfirm();
    }
  };

  return (
    <PopupContainer>
      <>
        {info && (
          <div css={popup}>
            {info?.imageLink && (
              <img src={info.imageLink} alt="Badge image" className="image" />
            )}

            <div className="new">
              {storyItems
                ? t('chat_popup_title_get_new_item')
                : t('home_popup_title_get_new_achievement')}
            </div>
            <div className="name">{info?.displayName || info?.name || ''}</div>
            <div className="desc">{info?.description || ''}</div>
            <Button
              title={
                storyItems
                  ? t('chat_popup_button_acquire')
                  : t('common_screen_title_confirm')
              }
              onClick={onPressConfirm}
            />
          </div>
        )}
      </>
    </PopupContainer>
  );
};

const popup = css`
  position: absolute;
  width: 90%;
  max-width: 760px;
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 12px;
  padding: 24px;

  .image {
    width: 10em;
    height: 10em;
    border-radius: 16px;
    object-fit: cover;
  }

  .new {
    margin-top: 1em;
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: -0.3px;
    color: ${theme.colors.primary500};
  }

  .name {
    margin-top: 4px;
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: bold;
    font-size: 20px;
    line-height: 28px;
    text-align: center;
    letter-spacing: -0.3px;
    color: ${theme.colors.gray900};
  }

  .desc {
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: -0.3px;
    color: ${theme.colors.gray600};
    margin-top: 4px;
    margin-bottom: 32px;
  }
`;

export default AchievementPopup;
